import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { CheckCircle, Lock, Play, Trophy, Target, FileText, Calendar, X, Check } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useCustomAuth } from '@/hooks/useCustomAuth'

interface QuizInterfaceProps {
  moduleId: string
  moduleTitle?: string
  isModuleCompleted?: boolean
}

interface Question {
  id: string
  question: string
  options: string[]
  order_index: number
}

interface QuizRecommendation {
  task_ids: string[]
  resource_ids: string[]
  event_ids: string[]
}

interface RecommendationItem {
  id: string
  title: string
  description?: string
  type: 'task' | 'resource' | 'event'
  added: boolean
}

const QuizInterface = ({ moduleId, moduleTitle, isModuleCompleted }: QuizInterfaceProps) => {
  const [quiz, setQuiz] = useState<any>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [loading, setLoading] = useState(true)
  const [quizCompleted, setQuizCompleted] = useState(false)
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [answers, setAnswers] = useState<number[]>([])
  const [showResults, setShowResults] = useState(false)
  const [showRecommendationsModal, setShowRecommendationsModal] = useState(false)
  const [recommendations, setRecommendations] = useState<QuizRecommendation | null>(null)
  const [recommendationItems, setRecommendationItems] = useState<RecommendationItem[]>([])
  const [loadingRecommendations, setLoadingRecommendations] = useState(false)
  const [showQuizModal, setShowQuizModal] = useState(false)
  const { toast } = useToast()

  const getTypeIcon = (type: 'task' | 'resource' | 'event') => {
    switch (type) {
      case 'task':
        return <Target className="w-5 h-5 text-blue-500" />
      case 'resource':
        return <FileText className="w-5 h-5 text-green-500" />
      case 'event':
        return <Calendar className="w-5 h-5 text-purple-500" />
      default:
        return <Trophy className="w-5 h-5 text-yellow-500" />
    }
  }

  const getTypeColor = (type: 'task' | 'resource' | 'event') => {
    switch (type) {
      case 'task':
        return 'border-blue-200 text-blue-700'
      case 'resource':
        return 'border-green-200 text-green-700'
      case 'event':
        return 'border-purple-200 text-purple-700'
      default:
        return 'border-gray-200 text-gray-700'
    }
  }

  const getButtonColor = (type: 'task' | 'resource' | 'event') => {
    switch (type) {
      case 'task':
        return 'bg-blue-600 hover:bg-blue-700'
      case 'resource':
        return 'bg-green-600 hover:bg-green-700'
      case 'event':
        return 'bg-purple-600 hover:bg-purple-700'
      default:
        return 'bg-gray-600 hover:bg-gray-700'
    }
  }

  useEffect(() => {
    if (moduleId) {
      checkQuizAvailability()
    }
  }, [moduleId])

  // Add effect to watch for changes in module completion status
  useEffect(() => {
    if (moduleId && isModuleCompleted !== undefined) {
      checkQuizAvailability()
    }
  }, [moduleId, isModuleCompleted])

  // Add effect to fetch quiz questions when the button is clicked
  useEffect(() => {
    if (showQuizModal && quiz) {
      console.log('QuizInterface - Modal opened, fetching questions for quiz:', quiz.id)
      fetchQuizQuestions(quiz.id)
    } else {
      console.log('QuizInterface - Modal effect not triggered:', { 
        showQuizModal, 
        hasQuiz: !!quiz, 
        questionsLength: questions.length 
      })
    }
  }, [showQuizModal, quiz])

  const checkQuizAvailability = async () => {
    try {
      setLoading(true)
      
      // Use the isModuleCompleted prop instead of checking progress manually
      if (isModuleCompleted) {
        // Get user's school_id to prefer school-specific quiz
        const { data: { session } } = await supabase.auth.getSession()
        let userSchoolId: string | null = null

        if (session) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('school_id')
            .eq('id', session.user.id)
            .single()
          
          userSchoolId = profile?.school_id || null
        }

        // Fetch quizzes for this module (both global and school-specific)
        let quizData = null

        // Prefer school-specific quiz if user has a school
        if (userSchoolId) {
          const { data: schoolQuiz, error: schoolQuizError } = await supabase
            .from('quizzes')
            .select('*')
            .eq('module_id', moduleId)
            .eq('school_id', userSchoolId)
            .maybeSingle()

          if (schoolQuizError && schoolQuizError.code !== 'PGRST116') throw schoolQuizError
          if (schoolQuiz) quizData = schoolQuiz
        }

        // Fallback to global quiz if no school-specific quiz found
        if (!quizData) {
          const { data: globalQuiz, error: globalQuizError } = await supabase
            .from('quizzes')
            .select('*')
            .eq('module_id', moduleId)
            .is('school_id', null)
            .maybeSingle()

          if (globalQuizError && globalQuizError.code !== 'PGRST116') throw globalQuizError
          if (globalQuiz) quizData = globalQuiz
        }

        if (quizData) {
          setQuiz(quizData)
          await fetchQuizQuestions(quizData.id)
          
          // Check if user has already completed this quiz
          if (session?.user) {
            const { data: attemptData, error: attemptError } = await supabase
              .from('quiz_attempts')
              .select('*')
              .eq('user_id', session.user.id)
              .eq('quiz_id', quizData.id)
              .single()

            if (!attemptError && attemptData) {
              setQuizCompleted(true)
              setAnswers(attemptData.answers || [])
              setShowResults(true)
              await fetchRecommendations(attemptData.answers || [])
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Error checking quiz availability:', error)
      toast({
        title: "Error",
        description: "Failed to check quiz availability",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchQuizQuestions = async (quizId: string) => {
    try {
      console.log('QuizInterface - fetchQuizQuestions called with quizId:', quizId)
      const { data, error } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('quiz_id', quizId)
        .order('order_index')

      if (error) throw error
      console.log('QuizInterface - Quiz questions fetched:', data)
      setQuestions(data || [])
    } catch (error: any) {
      console.error('Error fetching quiz questions:', error)
      toast({
        title: "Error",
        description: "Failed to fetch quiz questions",
        variant: "destructive",
      })
    }
  }

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...answers]
    newAnswers[currentQuestion] = answerIndex
    setAnswers(newAnswers)
  }

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1)
    } else {
      // Quiz completed
      submitQuiz()
    }
  }

  const handlePreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1)
    }
  }

  const submitQuiz = async () => {
    try {
      if (!quiz) return

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        toast({
          title: "Error",
          description: "Please log in to complete the quiz",
          variant: "destructive",
        })
        return
      }

      // Save quiz attempt
      const { error: attemptError } = await supabase
        .from('quiz_attempts')
        .insert({
          user_id: session.user.id,
          quiz_id: quiz.id,
          answers: answers,
          score: 0, // We don't need scoring for this system
          total_questions: questions.length
        })

      if (attemptError) throw attemptError

      // Mark quiz as completed
      setQuizCompleted(true)
      setShowResults(true)
      await fetchRecommendations(answers)
      
      // Close quiz modal and open recommendations modal
      setShowQuizModal(false)
      setShowRecommendationsModal(true)
      
      toast({ title: "Quiz completed! Check your personalized recommendations below." })
    } catch (error: any) {
      console.error('Error submitting quiz:', error)
      toast({
        title: "Error",
        description: "Failed to submit quiz",
        variant: "destructive",
      })
    }
  }

  const resetQuiz = () => {
    setCurrentQuestion(0)
    setAnswers([])
    setQuizCompleted(false)
    setShowResults(false)
    setShowQuizModal(false)
    setShowRecommendationsModal(false)
  }

  const fetchRecommendations = async (userAnswers: number[]) => {
    try {
      if (!quiz) return

      // Convert answers to pattern (e.g., [0, 1, 2] -> "ABC")
      const answerPattern = userAnswers.map(answer => String.fromCharCode(65 + answer)).join('')
      console.log('QuizInterface - Fetching recommendations for pattern:', answerPattern)

      // Get user's school_id to prefer school-specific recommendations
      const { data: { session } } = await supabase.auth.getSession()
      let userSchoolId: string | null = null

      if (session) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('school_id')
          .eq('id', session.user.id)
          .single()
        
        userSchoolId = profile?.school_id || null
      }

      // Get recommendations for this pattern - prefer school-specific, fallback to global
      let data = null

      // Try school-specific recommendations first
      if (userSchoolId) {
        const { data: schoolRecs, error: schoolRecsError } = await supabase
          .from('quiz_answer_mappings')
          .select('task_ids, resource_ids, event_ids')
          .eq('quiz_id', quiz.id)
          .eq('answer_pattern', answerPattern)
          .eq('school_id', userSchoolId)
          .maybeSingle()

        if (schoolRecsError && schoolRecsError.code !== 'PGRST116') throw schoolRecsError
        if (schoolRecs) data = schoolRecs
      }

      // Fallback to global recommendations if no school-specific found
      if (!data) {
        const { data: globalRecs, error: globalRecsError } = await supabase
          .from('quiz_answer_mappings')
          .select('task_ids, resource_ids, event_ids')
          .eq('quiz_id', quiz.id)
          .eq('answer_pattern', answerPattern)
          .is('school_id', null)
          .maybeSingle()

        if (globalRecsError && globalRecsError.code !== 'PGRST116') throw globalRecsError
        if (globalRecs) data = globalRecs
      }

      if (data) {
        console.log('QuizInterface - Found recommendations:', data)
        setRecommendations(data)
        
        // Create user inspirations for these recommendations
        await createUserInspirations(data, answerPattern)
        
        await fetchRecommendationDetails(data)
      } else {
        console.log('QuizInterface - No recommendations found for pattern:', answerPattern)
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error)
    }
  }

  const createUserInspirations = async (recs: QuizRecommendation, answerPattern: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) return

      console.log('QuizInterface - Creating user inspirations for pattern:', answerPattern)

      // Create inspirations for tasks
      if (recs.task_ids && recs.task_ids.length > 0) {
        for (const taskId of recs.task_ids) {
          // Check if this inspiration already exists (regardless of answer pattern)
          const { data: existingTask } = await supabase
            .from('user_inspirations')
            .select('id')
            .eq('user_id', session.user.id)
            .eq('source_quiz_id', quiz?.id)
            .eq('task_id', taskId)
            .single()

          // Only insert if it doesn't exist
          if (!existingTask) {
            const { error } = await supabase
              .from('user_inspirations')
              .insert({
                user_id: session.user.id,
                source_quiz_id: quiz?.id,
                answer_pattern: answerPattern,
                task_id: taskId,
                created_at: new Date().toISOString()
              })
            
            if (error) {
              console.error('Error creating task inspiration:', error)
            }
          } else {
            console.log('Task inspiration already exists, skipping:', taskId)
          }
        }
      }

      // Create inspirations for resources
      if (recs.resource_ids && recs.resource_ids.length > 0) {
        for (const resourceId of recs.resource_ids) {
          // Check if this inspiration already exists (regardless of answer pattern)
          const { data: existingResource } = await supabase
            .from('user_inspirations')
            .select('id')
            .eq('user_id', session.user.id)
            .eq('source_quiz_id', quiz?.id)
            .eq('resource_id', resourceId)
            .single()

          // Only insert if it doesn't exist
          if (!existingResource) {
            const { error } = await supabase
              .from('user_inspirations')
              .insert({
                user_id: session.user.id,
                source_quiz_id: quiz?.id,
                answer_pattern: answerPattern,
                resource_id: resourceId,
                created_at: new Date().toISOString()
              })
            
            if (error) {
              console.error('Error creating resource inspiration:', error)
            }
          } else {
            console.log('Resource inspiration already exists, skipping:', resourceId)
          }
        }
      }

      // Create inspirations for events
      if (recs.event_ids && recs.event_ids.length > 0) {
        for (const eventId of recs.event_ids) {
          // Check if this inspiration already exists (regardless of answer pattern)
          const { data: existingEvent } = await supabase
            .from('user_inspirations')
            .select('id')
            .eq('user_id', session.user.id)
            .eq('source_quiz_id', quiz?.id)
            .eq('event_id', eventId)
            .single()

          // Only insert if it doesn't exist
          if (!existingEvent) {
            const { error } = await supabase
              .from('user_inspirations')
              .insert({
                user_id: session.user.id,
                source_quiz_id: quiz?.id,
                answer_pattern: answerPattern,
                event_id: eventId,
                created_at: new Date().toISOString()
              })
            
            if (error) {
              console.error('Error creating event inspiration:', error)
            }
          } else {
            console.log('Event inspiration already exists, skipping:', eventId)
          }
        }
      }

      console.log('QuizInterface - User inspirations created successfully')
    } catch (error) {
      console.error('Error creating user inspirations:', error)
    }
  }

  const fetchRecommendationDetails = async (recs: QuizRecommendation) => {
    try {
      setLoadingRecommendations(true)
      const items: RecommendationItem[] = []

      // Fetch task details
      if (recs.task_ids && recs.task_ids.length > 0) {
        const { data: tasks, error: tasksError } = await supabase
          .from('admin_tasks')
          .select('id, title, description')
          .in('id', recs.task_ids)

        if (!tasksError && tasks) {
          tasks.forEach(task => {
            items.push({
              id: task.id,
              title: task.title,
              description: task.description,
              type: 'task',
              added: false
            })
          })
        }
      }

      // Fetch resource details
      if (recs.resource_ids && recs.resource_ids.length > 0) {
        const { data: resources, error: resourcesError } = await supabase
          .from('admin_resources')
          .select('id, title, description')
          .in('id', recs.resource_ids)

        if (!resourcesError && resources) {
          resources.forEach(resource => {
            items.push({
              id: resource.id,
              title: resource.title,
              description: resource.description,
              type: 'resource',
              added: false
            })
          })
        }
      }

      // Fetch event details
      if (recs.event_ids && recs.event_ids.length > 0) {
        const { data: events, error: eventsError } = await supabase
          .from('events')
          .select('id, title, description')
          .in('id', recs.event_ids)

        if (!eventsError && events) {
          events.forEach(event => {
            items.push({
              id: event.id,
              title: event.title,
              description: event.description,
              type: 'event',
              added: false
            })
          })
        }
      }

      setRecommendationItems(items)
    } catch (error) {
      console.error('Error fetching recommendation details:', error)
    } finally {
      setLoadingRecommendations(false)
    }
  }

  const addToMyList = async (type: 'task' | 'resource' | 'event', id: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        toast({
          title: "Error",
          description: "Please log in to add items to your list",
          variant: "destructive",
        })
        return
      }

      // Find the item to get its title
      const item = recommendationItems.find(item => item.id === id)
      if (!item) return

      if (type === 'task') {
        // Add to custom_tasks
        const { error } = await supabase
          .from('custom_tasks')
          .insert({
            user_id: session.user.id,
            title: item.title,
            description: item.description,
            status: 'active',
            module: 'Quiz Recommendation',
            domain: 'Personalized'
          })
        if (error) throw error
      } else if (type === 'resource') {
        // Add to user_resources
        const { error } = await supabase
          .from('user_resources')
          .insert({
            user_id: session.user.id,
            title: item.title,
            description: item.description,
            status: 'active',
            module: 'Quiz Recommendation',
            domain: 'Personalized'
          })
        if (error) throw error
      } else if (type === 'event') {
        // Add to user_events
        const { error } = await supabase
          .from('user_events')
          .insert({
            user_id: session.user.id,
            event_id: id,
            status: 'active'
          })
        if (error) throw error
      }

      // Update the UI to show this item was added
      setRecommendationItems(prev => 
        prev.map(item => 
          item.id === id ? { ...item, added: true } : item
        )
      )

      toast({ 
        title: "Added to your list!", 
        description: `${item.title} has been added to your ${type}s. Use the navigation buttons to view it!` 
      })
    } catch (error: any) {
      console.error('Error adding to list:', error)
      toast({
        title: "Error",
        description: "Failed to add to your list",
        variant: "destructive",
      })
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">Module Quiz</CardTitle>
          <CardDescription>Checking quiz availability...</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  if (!quiz) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">Module Quiz</CardTitle>
          <CardDescription>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Lock className="w-4 h-4" />
              Quiz will unlock after completing all videos in this module
            </div>
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  // Show "Take Assessment" button when module is completed (even if quiz was taken before)
  if (isModuleCompleted) {
    console.log('QuizInterface - Rendering Take Assessment button (module completed):', { 
      isModuleCompleted, 
      quizCompleted, 
      quiz: !!quiz, 
      questions: questions.length,
      showQuizModal 
    })
    
    // Show quiz interface when button is clicked
    if (showQuizModal && questions.length > 0) {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-500" />
              Knowledge Assessment: {moduleTitle}
            </CardTitle>
            <CardDescription>
              Question {currentQuestion + 1} of {questions.length}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              />
            </div>

            {/* Current Question */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">{questions[currentQuestion]?.question}</h3>
              
              <div className="space-y-3">
                {questions[currentQuestion]?.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    className={`w-full p-4 text-left border rounded-lg transition-all ${
                      answers[currentQuestion] === index
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="w-8 h-8 flex items-center justify-center">
                        {String.fromCharCode(65 + index)}
                      </Badge>
                      <span>{option}</span>
                      {answers[currentQuestion] === index && (
                        <CheckCircle className="w-5 h-5 text-blue-500 ml-auto" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                disabled={currentQuestion === 0}
              >
                Previous
              </Button>
              
              <Button
                onClick={() => {
                  if (currentQuestion < questions.length - 1) {
                    setCurrentQuestion(currentQuestion + 1)
                  } else {
                    // Quiz completed
                    submitQuiz()
                    setShowQuizModal(false)
                  }
                }}
                disabled={answers[currentQuestion] === undefined}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {currentQuestion === questions.length - 1 ? 'Complete Quiz' : 'Next'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )
    }
    
    // Show the button when quiz modal is not open
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-500" />
            Knowledge Assessment
          </CardTitle>
          <CardDescription>
            {quizCompleted 
              ? "You've completed this assessment before. Click below to retake it and get new recommendations!"
              : "You've completed all videos in this module. Take the assessment to unlock personalized recommendations!"
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={() => {
              console.log('Take Assessment button clicked!')
              console.log('Before setShowQuizModal - showQuizModal:', showQuizModal)
              setShowQuizModal(true)
              console.log('After setShowQuizModal - showQuizModal should be true')
              // Force a re-render to see if state changes
              setTimeout(() => {
                console.log('After timeout - showQuizModal:', showQuizModal)
              }, 100)
            }}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            <Target className="w-4 h-4 mr-2" />
            {quizCompleted ? 'Retake Assessment' : 'Take Assessment'}
          </Button>
          
          {/* Show previous results if quiz was completed before */}
          {quizCompleted && (
            <div className="text-center pt-4 border-t">
              <p className="text-sm text-gray-600 mb-3">
                You've completed this assessment before. View your previous recommendations:
              </p>
              <Button 
                onClick={() => setShowRecommendationsModal(true)}
                variant="outline"
                className="w-full"
              >
                <Trophy className="w-4 h-4 mr-2" />
                View Previous Recommendations
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  if (questions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">Module Quiz</CardTitle>
          <CardDescription>No questions configured for this quiz yet.</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  // Don't render anything else - quiz will be shown inline above
  return (
    <>
      {/* Quiz Modal */}
      <Dialog open={showQuizModal} onOpenChange={setShowQuizModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-500" />
              Knowledge Assessment: {moduleTitle}
            </DialogTitle>
            <DialogDescription>
              Question {currentQuestion + 1} of {questions.length}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              />
            </div>

            {/* Current Question */}
            {questions.length > 0 ? (
              <>
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">{questions[currentQuestion]?.question}</h3>
                  
                  <div className="space-y-3">
                    {questions[currentQuestion]?.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswerSelect(index)}
                        className={`w-full p-4 text-left border rounded-lg transition-all ${
                          answers[currentQuestion] === index
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="w-8 h-8 flex items-center justify-center">
                            {String.fromCharCode(65 + index)}
                          </Badge>
                          <span>{option}</span>
                          {answers[currentQuestion] === index && (
                            <CheckCircle className="w-5 h-5 text-blue-500 ml-auto" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                    disabled={currentQuestion === 0}
                  >
                    Previous
                  </Button>
                  
                  <Button
                    onClick={() => {
                      if (currentQuestion < questions.length - 1) {
                        setCurrentQuestion(currentQuestion + 1)
                      } else {
                        // Quiz completed
                        submitQuiz()
                        setShowQuizModal(false)
                      }
                    }}
                    disabled={answers[currentQuestion] === undefined}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {currentQuestion === questions.length - 1 ? 'Complete Quiz' : 'Next'}
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading quiz questions...</p>
                <p className="text-sm text-gray-500 mt-2">Please wait while we prepare your assessment.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Recommendations Modal */}
      <Dialog open={showRecommendationsModal} onOpenChange={setShowRecommendationsModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Your Personalized Recommendations
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {loadingRecommendations ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your recommendations...</p>
              </div>
            ) : recommendationItems.length === 0 ? (
              <div className="text-center py-8">
                <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No recommendations found</h3>
                <p className="text-gray-600">
                  We couldn't find specific recommendations for your answer pattern. 
                  Try taking the quiz again with different answers!
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Summary */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h3 className="font-semibold text-purple-900 mb-2">
                    🎯 Quiz Results Summary
                  </h3>
                  <p className="text-purple-700 text-sm">
                    Based on your answers, we've identified {recommendationItems.length} personalized recommendations. 
                    You can add any of these to your personal lists below.
                  </p>
                </div>

                {/* Added Items Summary */}
                {recommendationItems.some(item => item.added) && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="font-semibold text-green-900 mb-2">
                      ✅ Items Added to Your Lists
                    </h3>
                    <div className="space-y-2 text-sm text-green-700">
                      {recommendationItems.filter(item => item.added).map(item => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          <span>
                            <strong>{item.title}</strong> added to My {item.type.charAt(0).toUpperCase() + item.type.slice(1)}s
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-green-600 text-xs mt-2">
                      💡 Use the navigation cards below to view your updated lists!
                    </p>
                  </div>
                )}

                {/* Recommendations List */}
                <div className="space-y-4">
                  <div className="text-center py-2">
                    <p className="text-sm text-gray-600">
                      💡 Click the buttons below to add items to your personal lists. 
                      You can then view them in the respective sections using the navigation cards.
                    </p>
                  </div>
                  
                  {recommendationItems.map((item, index) => (
                    <div 
                      key={item.id} 
                      className={`border rounded-lg p-4 transition-all ${
                        item.added 
                          ? 'border-green-200 bg-green-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {getTypeIcon(item.type)}
                            
                            <Badge 
                              variant="outline" 
                              className={`${getTypeColor(item.type)}`}
                            >
                              {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                            </Badge>
                            
                            {item.added && (
                              <Badge className="bg-green-100 text-green-800 border-green-200">
                                <Check className="w-3 h-3 mr-1" />
                                Added
                              </Badge>
                            )}
                          </div>
                          
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {item.title}
                          </h4>
                          
                          {item.description && (
                            <p className="text-gray-600 text-sm">
                              {item.description}
                            </p>
                          )}
                        </div>
                        
                        {!item.added && (
                          <Button
                            onClick={() => addToMyList(item.type, item.id)}
                            size="sm"
                            className={getButtonColor(item.type)}
                          >
                            Add to My {item.type.charAt(0).toUpperCase() + item.type.slice(1)}s
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Navigation Cards */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 text-center">
                    🧭 Navigate to Your Lists
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {recommendationItems.some(item => item.type === 'task') && (
                      <div 
                        onClick={() => {
                          setShowRecommendationsModal(false)
                          window.location.href = '/tasks'
                        }}
                        className="bg-blue-50 border border-blue-200 rounded-lg p-4 cursor-pointer hover:bg-blue-100 transition-colors"
                      >
                        <div className="text-center">
                          <Target className="w-12 h-12 text-blue-500 mx-auto mb-2" />
                          <h4 className="font-semibold text-blue-900 mb-1">My Tasks</h4>
                          <p className="text-blue-700 text-sm">
                            View and manage your personal tasks
                          </p>
                          <Badge className="mt-2 bg-blue-100 text-blue-800">
                            {recommendationItems.filter(item => item.type === 'task').length} items
                          </Badge>
                        </div>
                      </div>
                    )}
                    
                    {recommendationItems.some(item => item.type === 'resource') && (
                      <div 
                        onClick={() => {
                          setShowRecommendationsModal(false)
                          window.location.href = '/resources'
                        }}
                        className="bg-green-50 border border-green-200 rounded-lg p-4 cursor-pointer hover:bg-green-100 transition-colors"
                      >
                        <div className="text-center">
                          <FileText className="w-12 h-12 text-green-500 mx-auto mb-2" />
                          <h4 className="font-semibold text-green-900 mb-1">My Resources</h4>
                          <p className="text-green-700 text-sm">
                            Access your saved learning resources
                          </p>
                          <Badge className="mt-2 bg-green-100 text-green-800">
                            {recommendationItems.filter(item => item.type === 'resource').length} items
                          </Badge>
                        </div>
                      </div>
                    )}
                    
                    {recommendationItems.some(item => item.type === 'event') && (
                      <div 
                        onClick={() => {
                          setShowRecommendationsModal(false)
                          window.location.href = '/events'
                        }}
                        className="bg-purple-50 border border-purple-200 rounded-lg p-4 cursor-pointer hover:bg-purple-100 transition-colors"
                      >
                        <div className="text-center">
                          <Calendar className="w-12 h-12 text-purple-500 mx-auto mb-2" />
                          <h4 className="font-semibold text-purple-900 mb-1">My Events</h4>
                          <p className="text-purple-700 text-sm">
                            Check your upcoming events
                          </p>
                          <Badge className="mt-2 bg-purple-100 text-purple-800">
                            {recommendationItems.filter(item => item.type === 'event').length} items
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowRecommendationsModal(false)}
                  >
                    Close
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={resetQuiz}
                  >
                    Take Quiz Again
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default QuizInterface
